import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Paginator } from '@app/classes/paginator';
import { BadgeOptions } from '@app/interfaces';

/**
 * IMPORTANT
 * This import must remain relative to prevent circular import issues
 * with parent components.
 *
 * Inspection suppressed to prevent automatic replacement.
 */
// noinspection ES6PreferShortImport
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PermissionDirective } from '@app/directives/permission.directive';
import { AppTranslatePipe } from '@app/pipes/app-translate.pipe';
import { Translatable } from '@app/types/translatable.type';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { WebBadgeComponent } from '../humi-design-library/badge/badge.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { ButtonComponent } from '../platform/button/button.component';
import { TabItemComponent } from '../tab-item/tab-item.component';

type ContextMenuType = 'semantic' | 'material';

type TranslatePipeParams = Parameters<TranslatePipe['transform']>;
@Component({
    selector: 'app-segment-header',
    templateUrl: './segment-header.template.html',
    standalone: true,
    imports: [
        TranslateModule,
        ButtonComponent,
        NgIf,
        WebBadgeComponent,
        MatMenuModule,
        NgTemplateOutlet,
        PaginationComponent,
        MatIconModule,
        AppTranslatePipe,
        PermissionDirective,
    ],
    styleUrls: ['./segment-header.style.scss'],
})
export class SegmentHeaderComponent implements OnInit, OnChanges {
    get activeTab(): TabItemComponent {
        return this._activeTab;
    }

    set activeTab(val: TabItemComponent) {
        this._activeTab = val;
        this.tab.emit(this._activeTab);
    }

    @Input() title: Translatable = 'components.segment-header.pageHeader';
    @Input() titleParams?: TranslatePipeParams[1];
    @Input() subtitle: Translatable;
    @Input() paginator: Paginator;
    @Input() collapsible = false;
    @Input() collapsed = false;
    @Input() editPermission: string | string[] = null;
    @Input() editFlag: boolean;
    @Input() editLabel: Translatable = 'edit';
    @Input() createPermission: string | string[] = null;
    @Input() createFlag: boolean;
    @Input() createLabel: Translatable = 'components.segment-header.create';
    @Input() allowEdit = true;
    @Input() tabMenu = false;
    @Input() initialTab: TabItemComponent;
    @Input() contextMenu = false;
    @Input() contextMenuType: ContextMenuType = 'semantic';
    @Input() id?: string;

    @Input() badgeOptions?: BadgeOptions;

    @Output() tab: EventEmitter<TabItemComponent> = new EventEmitter<TabItemComponent>();
    @Output() edit: EventEmitter<null> = new EventEmitter<null>();
    @Output() create: EventEmitter<null> = new EventEmitter<null>();

    // makes our title attribute not show up in the tooltip
    // https://stackoverflow.com/a/74510560
    @HostBinding('attr.title') get getTitle(): null {
        return null;
    }

    showEdit = false;
    showCreate = false;

    private _activeTab: TabItemComponent;

    constructor(private element: ElementRef) {
        if (this.editFlag !== undefined) {
            this.editPermission = ['never'];
        }
        if (this.createFlag !== undefined) {
            this.createPermission = ['never'];
        }
    }

    ngAfterContentInit(): void {
        setTimeout(() => {
            $(this.element.nativeElement).find('.ui.dropdown').dropdown();
        });
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.showEdit = this.allowEdit && this.edit.observers.length > 0;
        this.showCreate = this.create.observers.length > 0;
    }

    ngOnInit(): void {
        if (this.collapsible && this.collapsed) {
            this.collapse();
        }

        if (this.initialTab) {
            this._activeTab = this.initialTab;
        }
    }

    emitEdit(): void {
        this.edit.emit();
    }

    emitCreate(): void {
        this.create.emit();
    }

    toggle(): void {
        if (!this.collapsible) {
            return;
        }

        this.collapsed ? this.expand() : this.collapse();
    }

    expand(): void {
        $(this.element.nativeElement).siblings('.content, .table').show();
        $(this.element.nativeElement).removeClass('collapsed');
        this.collapsed = false;
    }

    collapse(): void {
        $(this.element.nativeElement).siblings('.content, .table').hide();
        $(this.element.nativeElement).addClass('collapsed');
        this.collapsed = true;
    }

    /**
     * The title input still allows the deprecated TranslatableObject.
     * This function just ensures that the non-deprecated ngx translate pipe can be used on the provided title
     */
    isTranslatableViaTranslatePipe(title: Translatable): title is TranslatePipeParams[0] {
        return typeof title === 'string';
    }
}
